import { CommonModule } from '@angular/common';
import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { CalendarModule } from 'primeng/calendar';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { TimesheetFilterComponent } from '../timesheet-filter/timesheet-filter.component';
import { LookupService } from '../../../../services/lookup.service';
import { UserService } from '../../../../services/user.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { ListService } from '../../../../shared/services/list.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { PopupDialogComponent } from '../../../../shared/components/popup-dialog/popup-dialog.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { TimesheetService } from '../../../../services/timesheet.service';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { AutoFocusHeaderDirective } from '../../../../shared/directives/auto-focus-header.directive';

@Component({
  selector: 'ppl-timesheet-payroll-adjustment-approvals',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgZorroModule,
    NzDatePickerModule,
    CalendarModule,
    ButtonComponent,
    ListTableComponent,
    TimesheetFilterComponent,
    PopupDialogComponent,
    AutoFocusHeaderDirective
  ],
  templateUrl: './timesheet-payroll-adjustment-approvals.component.html',
  styleUrl: './timesheet-payroll-adjustment-approvals.component.css'
})
export class TimesheetPayrollAdjustmentApprovalsComponent {
  @Input() source: string = 'payroll';
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
  @ViewChild(TimesheetFilterComponent) timesheetFilterChildComponent!: TimesheetFilterComponent;
  @ViewChild('searchFilterWarningDialog') searchFilterWarningDialog!: TemplateRef<any>;

  private dialogSvc = inject(DialogService);

  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  appliedFilters: any = [];
  approvalTimesheetForm!: FormGroup;
  dialogButtonLabel = '';
  commentText = '';
  dialogData: any;
  dialogNoteMessage = '';
  isDialogNoteDisplayed = false;
  programName!: string;
  processingStatusList: any;
  reasonList: any;
  businessStatusList: any;
  selectedAction: any;
  serviceCodes: LookupValueItem[] = [];
  showActionPopup: boolean = false;
  timesheets: any[] = [];
  title = '';
  chequeNumbers: any[] = [];

  buttons = [
    {
      button_id: "Approve",
      button_label: "Approve",
      buttonAction: "approveTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "ApproveAll",
      button_label: "Approve All",
      buttonAction: "approveAllTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "Reject",
      button_label: "Reject",
      buttonAction: "rejectTimesheet",
      className: "primary-btn-color"
    },
    {
      button_id: "RejectAll",
      button_label: "Reject All",
      buttonAction: "rejectAllTimesheets",
      className: "primary-btn-color"
    }
  ];

  cols = [
    {
      field: 'service_date',
      header: 'Service \nDate',
      jsonpath: 'service_date',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'service_description',
      header: 'Service \nDescription',
      jsonpath: 'service_description',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumer_name',
      header: 'Consumer \nName',
      jsonpath: 'consumer_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'participant_ppl_id',
      header: 'Consumer \nPPL ID',
      jsonpath: 'participant_ppl_id',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'provider_name',
      header: 'PA \nName',
      jsonpath: 'provider_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'provider_ppl_id',
      header: 'PA PPL ID',
      jsonpath: 'provider_ppl_id',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'timeInTimeOut',
      header: 'Time In - \nTime Out',
      jsonpath: 'timeInTimeOut',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'billed_units',
      header: 'Billed \nUnits \n(hrs)',
      jsonpath: 'billed_units',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paid_units',
      header: 'Paid \nUnits',
      jsonpath: 'paid_units',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'servicecode_rate',
      header: 'Service \nRate',
      jsonpath: 'servicecode_rate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'businessStatus',
      header: 'Business \nStatus',
      jsonpath: 'businessStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'processingStatus',
      header: 'Processing \nStatus',
      jsonpath: 'processingStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'source_type_lkp',
      header: 'Timesheet \nSource',
      jsonpath: 'source_type_lkp',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'mco_name',
      header: 'MCO \nName',
      jsonpath: 'mco_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'client_auth_number',
      header: 'Authorization \nNumber',
      jsonpath: 'client_auth_number',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'cba',
      header: 'CBA \nAssociation',
      jsonpath: 'cba',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  lineItemCols = [
    {
      field: 'adjcreatedate',
      header: 'Date',
      jsonpath: 'adjcreatedate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'service_description',
      header: 'Service Description',
      jsonpath: 'service_description',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumer_name',
      header: 'Consumer Name',
      jsonpath: 'consumer_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'participant_ppl_id',
      header: 'Consumer PPL ID',
      jsonpath: 'participant_ppl_id',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'provider_name',
      header: 'Provider Name',
      jsonpath: 'provider_name',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'provider_ppl_id',
      header: 'Provider PPL ID',
      jsonpath: 'provider_ppl_id',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
      isClickable: true
    },
    {
      field: 'timeInTimeOut',
      header: 'Time In - Time Out',
      jsonpath: 'timeInTimeOut',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'billed_units',
      header: 'Billed Units (hrs)',
      jsonpath: 'billed_units',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paid_units',
      header: 'Paid Units',
      jsonpath: 'paid_units',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'servicecode_rate',
      header: 'Service Rate',
      jsonpath: 'servicecode_rate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'businessStatus',
      header: 'Business Status',
      jsonpath: 'businessStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'processingStatus',
      header: 'Processing Status',
      jsonpath: 'processingStatus',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  fields = [
    { field: 'consumer', show: true },
    { field: 'consumerPplId', show: true },
    { field: 'provider', show: true },
    { field: 'providerPplId', show: true },
    { field: 'payPeriod', show: true, multiple: true, hideFuturePeriods: true },
    { field: 'paymentDate', show: true },
    { field: 'serviceCode', show: true },
    { field: 'chequeNumber', show: true },
    { field: 'serviceDate', show: true, hideFuturePeriods: true },
  ];

  keyMapping: { [key: string]: string } = {
    Timeframe: "serviceDuration",
    serviceDate: "service_date",
    payPeriod: "tas_master_payroll_schedule_id",
    consumer: "consumer_name",
    consumerPplId: "participant_ppl_id",
    provider: "provider_name",
    providerPplId: "provider_ppl_id",
    chequeNumber: "cheque_number",
    paymentDate: "paid_tas_master_payroll_schedule_id",
    serviceCode: "servicecode_detail_id"
  };

  constructor(
    private dateTimeFormatterService: DateTimeFormatterService,
    private errorAlertService: ErrorAlertService,
    private fb: FormBuilder,
    private listService: ListService,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.businessStatusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.reasonList = this.lookupService.getLookupValue('Disposition Reason');
    this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');

    this.approvalTimesheetForm = this.fb.group({
      comments: [null]
    });
  }

  searchTimesheets(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false) {
    if (startingPageNumber == 1) {
      this.timesheets = [];
    }

    const adjustedTimesheetPayload = this.buildRequestPayload(
      this.source === 'payroll' ? 'payrollDecisionList' : 'billingDecisionList',
      'tas_timesheetline_detail_id',
      filters,
      startingPageNumber
    );

    this.listService
      .getListData(adjustedTimesheetPayload, 'claims')
      .subscribe((data: any) => {
        const records = this.processData(data.responsedata);
        if (isChequeLookUp) {
          if (records && records?.length) {
           this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.cheque_number).map((e: any) => e.cheque_number)));
          }
        }
        else {
          if (startingPageNumber > 1) {
            this.timesheets = [...this.timesheets, ...records];
          } else {
            this.timesheets = records;
          }

          if (this.childComponent) {
            this.childComponent.resetPagination(startingPageNumber, currentPage);
          }

          if (this.timesheets?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.cheque_number).map((e: any) => e.cheque_number)));
          }
        }
      });
  }

  searchSubRowTimesheets(filters: any, event: any, startingPageNumber = 1, currentPage = 0) {
    const adjustedTimesheetPayload = this.buildRequestPayload(
      'adjustmentTimesheetList',
      'tas_timesheetline_detail_id',
      filters,
      startingPageNumber
    );

    this.listService
      .getListData(adjustedTimesheetPayload, 'claims')
      .subscribe((data: any) => {
        event.lineItems = data.responsedata;

        event?.lineItems?.forEach((element: any) => {
          element.timeInTimeOut = this.dateTimeFormatterService.convertTo12HourFormat(element.time_in) + ' - ' + this.dateTimeFormatterService.convertTo12HourFormat(element.time_out),
            element.businessStatus = this.businessStatusList.filter((status: any) => status.lkpValueCode === element.business_status_lkp)
              .map((status: any) => status.lkpValueName),
            element.processingStatus = this.processingStatusList.filter((status: any) => status.lkpValueCode === element.prcocessing_status_lkp)
              .map((status: any) => status.lkpValueName)
        });

        event.showLineItem = true;
        event.loaded = true;
      });
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };

    return payload;
  }

  processData(data: any) {
    if (data && data.length > 0) {
      data.forEach((element: any) => {
        element.timeInTimeOut = this.dateTimeFormatterService.convertTo12HourFormat(element.time_in) + ' - ' + this.dateTimeFormatterService.convertTo12HourFormat(element.time_out),
          element.businessStatus = this.businessStatusList.filter((status: any) => status.lkpValueCode === element.business_status_lkp)
            .map((status: any) => status.lkpValueName),
          element.processingStatus = this.processingStatusList.filter((status: any) => status.lkpValueCode === element.prcocessing_status_lkp)
            .map((status: any) => status.lkpValueName)
      });
    }

    return data;
  }

  onSearch(event: any) {
    const filters = [];

    if (event.serviceDate) {
      filters.push({
        jsonPath: "service_date",
        value: this.dateTimeFormatterService.formatDateToString(event.serviceDate as Date),
        operator: "Equal"
      });
    }

    if (event.payPeriod && event.payPeriod.length > 0) {
      filters.push({
        jsonPath: "tas_master_payroll_schedule_id",
        values: event.payPeriod,
        operator: "In"
      });
    }

    if (event.consumer && event.consumer.trim()) {
      filters.push({
        jsonPath: "consumer_name",
        value: event.consumer.trim(),
        operator: "equal"
      });
    }

    if (event.consumerPplId && event.consumerPplId.trim()) {
      filters.push({
        jsonPath: "participant_ppl_id",
        value: event.consumerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.provider && event.provider.trim()) {
      filters.push({
        jsonPath: "provider_name",
        value: event.provider.trim(),
        operator: "equal"
      });
    }

    if (event.providerPplId && event.providerPplId.trim()) {
      filters.push({
        jsonPath: "provider_ppl_id",
        value: event.providerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.chequeNumber && event.chequeNumber.length) {
      filters.push({
        jsonPath: "cheque_number",
        values: event.chequeNumber,
        operator: "In"
      });
    }

    if (event.paymentDate) {
      filters.push({
        jsonPath: "paid_tas_master_payroll_schedule_id",
        value: event.paymentDate,
        operator: "Equal"
      });
    }

    if (event.serviceCode) {
      filters.push({
        jsonPath: "servicecode_detail_id",
        value: event.serviceCode,
        operator: "Equal"
      });
    }

    const hasRelevantFilter = filters.some(filter =>
      ["provider_name", "consumer_name", "provider_ppl_id", "participant_ppl_id"].includes(filter.jsonPath)
    );

    if (!hasRelevantFilter) {
      this.openWarningMessage();
      return;
    }

    this.appliedFilters = filters;
    this.searchTimesheets(filters, undefined, undefined, event.chequeLookUp);
  }

  openWarningMessage() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      showOkButton: true,
      isOkText: 'Ok',
      size: 'medium',
      position: 'top',
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.searchFilterWarningDialog,
      dialogConfig,
      () => { },
      () => { }
    );
  }

  rowExpanded(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }

    const subRowFilter = [];
    subRowFilter.push({
      jsonPath: "parent_tas_timesheetline_detail_id",
      value: event.tas_timesheetline_detail_id,
      operator: "Equal"
    });

    this.searchSubRowTimesheets(subRowFilter, event);
  }

  areArraysEqual(a: any[], b: any[]): boolean {
    if (!Array.isArray(a) || !Array.isArray(b))
      return false;

    if (a.length !== b.length)
      return false;

    return a.every((val, i) => val === b[i]);
  }

  isFilterValid(timesheetFilterValues: any, searchCallbackFilterValues: any) {
    const entries = Object.entries(timesheetFilterValues);

    const hasValidFilter = entries.some(([_, value]) => {
      return value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
    });

    if (searchCallbackFilterValues?.length > 0 && !hasValidFilter) {
      return false;
    }

    const activeFilters = Object.keys(timesheetFilterValues).filter(key => {
      const value = timesheetFilterValues[key];
      if (value === null || value === undefined)
        return false;

      if (typeof value === 'string' && value.trim() === '')
        return false;

      if (Array.isArray(value) && value.length === 0)
        return false;

      return true;
    });

    if (activeFilters.length !== searchCallbackFilterValues.length)
      return false;

    return entries
      .filter(([_, value]) => value !== null && value !== "")
      .every(([key, value]: [any, any]) => {
        const mappedKey = this.keyMapping[key] || key;

        if (Array.isArray(value) && value.length === 0)
          return true;

        let comparisonValue: any;

        if (mappedKey === 'service_date') {
          comparisonValue = this.dateTimeFormatterService.formatDateToString(value as Date);
        }
        else if (mappedKey === 'serviceDuration') {
          comparisonValue = value.substr(0, value.indexOf(' '));
        } else {
          comparisonValue = value;
        };

        return searchCallbackFilterValues.some((filter: any) =>
          filter.jsonPath === mappedKey && (
            Array.isArray(filter.value || filter.values) ? this.areArraysEqual(filter.values, comparisonValue) :
              typeof filter?.value === 'string' ? filter?.value?.trim() === comparisonValue?.trim() :
                filter?.value === comparisonValue
          ));
      });
  }

  gridAction(event: any) {
    const { button, selectedCheckBox } = event;

    if (!this.isFilterValid(this.timesheetFilterChildComponent.timesheetSearchForm.value, this.appliedFilters)) {
      const errorMessage = `Some filters are newly applied but the search is not performed. Kindly perform search with updated filter or remove the filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (this.appliedFilters.length === 0 && (button.buttonAction === 'approveAllTimesheet' || button.buttonAction === 'rejectAllTimesheets')) {
      const errorMessage = `${button.button_label} action requires selection of filters. Kindly apply atleast one filter and perform the search with updated filter.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox && selectedCheckBox.length === 0
      && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Kindly select checkbox and then perform this action.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (selectedCheckBox.length > 1 && (button.buttonAction === 'approveTimesheet' || button.buttonAction === 'rejectTimesheet')) {
      const errorMessage = `Approve / Reject operation can be performed on single timesheet at a time.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (button) {
      this.dialogData = { payload: {} };
      switch (button.buttonAction) {
        case 'approveTimesheet':
        case 'rejectTimesheet': {
          const selectedTimesheet = selectedCheckBox[0];
          this.dialogData.payload.timesheetIds = [selectedTimesheet.tas_timesheetline_detail_id];
          this.isDialogNoteDisplayed = false;
          break;
        }
        case 'approveAllTimesheet':
        case 'rejectAllTimesheets': {
          if (selectedCheckBox.length > 0) {
            this.dialogData.payload.timesheetIds = selectedCheckBox.map((entry: any) => entry.tas_timesheetline_detail_id);
            this.dialogNoteMessage = `Note: The ${button.button_label} action will be performed on all selected timesheets.`
          } else {
            this.dialogData.payload.searchParam = this.appliedFilters;
            this.dialogNoteMessage = `Note: The ${button.button_label} action will be performed on all timesheets matching the selected search criteria.`;
          }
          this.isDialogNoteDisplayed = true;
          break;
        }
      }

      this.dialogData.button = button;
      this.dialogButtonLabel = button.button_label;
      this.title = `${this.source.trim().charAt(0).toUpperCase() + this.source.trim().slice(1)} Adjustment ${button.button_label}`;
      this.commentText = button.button_label.indexOf('Approve') > -1 ? 'Approval comment' : 'Rejection comment *';

      if (button.buttonAction === 'rejectTimesheet' || button.buttonAction === 'rejectAllTimesheets') {
        this.approvalTimesheetForm.get('comments')?.setValidators([Validators.required]);
      } else {
        this.approvalTimesheetForm.get('comments')?.setValidators(null);
      }
      this.approvalTimesheetForm.get('comments')?.setValue(null);
      this.approvalTimesheetForm.markAsUntouched();
      this.approvalTimesheetForm.get('comments')?.updateValueAndValidity();
      this.showActionPopup = true;
    }
  }

  onSave() {
    if (this.approvalTimesheetForm.valid) {
      let placeholder = '';
      const urlEndpoint = this.source === 'payroll' ? ENDPOINTS.CLAIMS.PAYROLL_TIMESHEET_DECISION : ENDPOINTS.CLAIMS.BILLING_TIMESHEET_DECISION;
      const payload = this.dialogData.payload;
      payload.comments = this.approvalTimesheetForm?.get('comments')?.value;

      switch (this.dialogData.button.buttonAction) {
        case 'approveTimesheet':
        case 'approveAllTimesheet': {
          payload.decision = 'A';
          placeholder = 'Timesheet approved';
          break;
        }
        case 'rejectTimesheet':
        case 'rejectAllTimesheets': {
          payload.decision = 'R';
          placeholder = 'Timesheet rejected';
          break;
        }
      }

      if (urlEndpoint) {
        this.timesheetService.adjustVoidTimesheet(urlEndpoint, this.programName, payload, placeholder)
          .subscribe((data: any) => {
            this.dialogData = null;
            this.showActionPopup = false;
            this.approvalTimesheetForm.reset();
            this.searchTimesheets(this.appliedFilters);
          });
      }
    } else {
      this.approvalTimesheetForm.markAllAsTouched();
    }
  }
}
